<template>
  <div
    class="screen"
    :class="{ collapsed, docked, tabbed, held: $slots.docked || banded || stateHeld }"
    :style="dockStyle"
  >
    <header ref="bar" class="bar">
      <div ref="column" class="leading">
        <template v-if="parentTitleKey">
          <button ref="button" class="back" type="button" @click="goBack">
            <IconChevronLeft ref="chevron" class="chevron" aria-hidden="true" />
            <!-- «Back» shown, where it leads still read after it: the name is «Back Trip» on every
                 step of the ladder, and the word shown starts it (review Р-2, owner's decision). -->
            <template v-if="fit === 'short'">
              <span class="label">{{ backWord }}</span>
              <span class="hidden">{{ ` ${parent}` }}</span>
            </template>
            <template v-else>
              <!-- The space is the hidden word's, so that it is read «Back Trip», not «BackTrip». -->
              <span class="hidden">{{ `${backWord} ` }}</span>
              <span :class="parentClass">{{ parent }}</span>
            </template>
          </button>
          <span class="samples" aria-hidden="true">
            <span ref="full">{{ parent }}</span>
            <span ref="short">{{ backWord }}</span>
          </span>
        </template>
        <div v-else-if="$slots.meta" class="meta" :aria-hidden="collapsed ? 'true' : undefined">
          <slot name="meta" />
        </div>
      </div>

      <!-- The large title below stays in the page, scrolled away or not, so a screen reader
           reads it there; this copy is only for the eye. -->
      <p class="small" aria-hidden="true">{{ title }}</p>

      <div class="trailing">
        <slot name="trailing" />
      </div>
    </header>

    <div class="head">
      <h1 class="title" tabindex="-1">{{ title }}</h1>
      <div v-if="$slots.subtitle" class="subtitle">
        <slot name="subtitle" />
      </div>
      <div ref="sentinel" class="sentinel" aria-hidden="true"></div>
    </div>

    <div class="notice-slot">
      <IdentityNotice />
    </div>

    <div ref="content" class="content">
      <slot />
      <!-- The room the strip below takes, and «Вернуть» over it, kept inside the scroll: the last
           row of a list has to be reachable, and both are over the page, not in it. -->
      <div
        v-if="$slots.docked || banded || stateHeld || $slots.undo"
        class="dock-room"
        :style="{ height: room }"
        aria-hidden="true"
      ></div>
    </div>

    <!-- Pinned above the tab bar, and the room for it is the frame's to keep: a screen that
         drew its own would part ways with the padding on the first change of its height. A new
         version waiting is its top row, over the screen's own main action (MOL-132, В-1).
         An error of the whole screen takes it over (К-1, 8c): its «Повторить» stands where the
         screen's action stood, and the row steps aside while the error offers «Обновить» itself;
         a version that did not take keeps its words, which offer nothing twice. -->
    <div v-if="$slots.docked || banded || stateHeld" ref="dock" class="dock">
      <UpdateBand v-if="banded" class="update" :class="{ over: $slots.docked || stateHeld }" />
      <div v-if="stateHeld" ref="stateTarget" class="state-actions"></div>
      <slot v-else name="docked" />
    </div>

    <!-- «Вернуть» stands in one place on every screen, 8 over the strip — or over the tab bar or the
         bottom edge where there is none (Ф-29, К-10). The frame's, not the strip's own: over the
         list and out of the strip, so the main action under it neither moves nor hides. -->
    <div v-if="$slots.undo" ref="undo" class="undo-place">
      <slot name="undo" />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUpdate, onUpdated, ref, watch } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import IconChevronLeft from '~icons/mdi/chevron-left'
import IdentityNotice from '@/components/IdentityNotice.vue'
import UpdateBand from '@/components/UpdateBand.vue'
import { useBackLabel } from '@/composables/useBackLabel'
import { useCollapsed, useHeight } from '@/composables/useCollapsed'
import { provideAnswerWord } from '@/composables/useAnswerWord'
import { provideStateStrip } from '@/composables/useStateStrip'
import { backTarget, useNavigation } from '@/navigation'
import { useLoginStore } from '@/stores/login'
import { usePwaUpdate } from '@/pwaUpdate'
import { focusScreenTitle } from '@/transitions'

/**
 * The frame every screen of 0.1 sits in: a pinned row, the large title, the room under the tab
 * bar. Four screens drawing their own would drift apart on the first change, and `App.vue`
 * cannot draw it for them — the trip puts its own buttons into the row.
 *
 * The row is pinned only when it has something in it: a back chevron, the trip's place and
 * «Finish». «What to buy» and «Ratings» have neither, and the mockup puts their title right
 * under the status bar — so there the row takes no room at rest and appears over the content,
 * holding the small title, once the large one has scrolled away.
 *
 * The chevron is labelled with the parent's title, never the word «Back»: the label says where
 * it leads. «Back» is still read out — as hidden text in front of the label, not an
 * `aria-label`, which would replace the visible word and leave a person saying «tap Trip» to
 * voice control with nothing to tap.
 *
 * At rest the row keeps no room for the small title it does not show, so the label has the
 * row. Once the title comes in, the label gives way first and by the iOS ladder — whole,
 * «Back», the chevron alone — and the title last, only when it alone does not fit between two
 * chevrons (MOL-75). The name does not follow the ladder: whatever is shown, the button is read
 * «Back Trip», the hidden part after the shown one — never «Back Back», and never a name that
 * loses where it leads as the page scrolls.
 */
export default defineComponent({
  name: 'AppScreen',
  components: { IconChevronLeft, IdentityNotice, UpdateBand },
  props: {
    title: { type: String, required: true },
  },
  setup(_props, { slots }) {
    const { t } = useI18n()
    const route = useRoute()
    const router = useRouter()

    const bar = ref<HTMLElement | null>(null)
    const dock = ref<HTMLElement | null>(null)
    const content = ref<HTMLElement | null>(null)
    const sentinel = ref<HTMLElement | null>(null)

    // Named by where it leads (`backTarget`): a screen opened from further up than its parent goes
    // back there, and says so (MOL-77) — the search of a record over «Покупки» says «Покупки».
    // Behind the door a public screen is named after it, «‹ Вход» (MOL-216).
    const login = useLoginStore()
    const parentTitleKey = computed(() => {
      const target = backTarget(router, route, login.closed)
      return target ? (target.titleKey ?? target.location.meta.titleKey) : undefined
    })

    const column = ref<HTMLElement | null>(null)
    const button = ref<HTMLElement | null>(null)
    const chevron = ref<ComponentPublicInstance | null>(null)
    const full = ref<HTMLElement | null>(null)
    const short = ref<HTMLElement | null>(null)
    const parent = computed(() => (parentTitleKey.value ? t(parentTitleKey.value) : ''))
    const backWord = computed(() => t('nav.back_label'))
    const fit = useBackLabel({
      column,
      button,
      chevron: computed(() => (chevron.value?.$el as Element | undefined) ?? null),
      full,
      short,
    })
    // Out of sight, the parent's title is still read after «Back».
    const parentClass = computed(() => (fit.value === 'none' ? 'hidden' : 'label'))
    // Slots are not reactive, so a computed would keep whatever it saw on mount — and the trip's
    // place and «Finish» arrive with the trip, from the API, after it. Read again before every
    // render instead: a row filled late is pinned and alive, not drawn inside an invisible one.
    const hasRow = (): boolean => Boolean(parentTitleKey.value ?? slots.meta ?? slots.trailing)
    const docked = ref(hasRow())
    const isReload = (element: Element | null) => element?.classList.contains('reload') ?? false
    /**
     * Where a focus lost in the strip goes: its first button but a «Обновить» (`.reload`) — a reload
     * the person did not ask for, the next Enter away (review №3, adversarial Г, Е) — unless the focus
     * stood on a «Обновить» itself, and then the one in its place (adversarial Ж).
     */
    const nextAction = (within: HTMLElement | null, onReload: boolean) => {
      const all = [...(within?.querySelectorAll('button') ?? [])]
      return (
        (onReload ? all.find((one) => isReload(one)) : undefined) ??
        all.find((one) => !isReload(one))
      )
    }
    // Whether the focus stood in the strip as this render began (adversarial Г1, below), and on what.
    let focusInDock = false
    let focusOnReload = false
    onBeforeUpdate(() => {
      docked.value = hasRow()
      focusInDock = dock.value?.contains(document.activeElement) ?? false
      focusOnReload = focusInDock && isReload(document.activeElement)
    })
    // A render that swapped the strip's actions under the focus — an account put away or deleted on
    // another phone, «Вернуть» answered — left it on the body: it goes to the strip's first button,
    // where the one it stood on was, or to the title where the strip went whole (MOL-194, Б2, Б3).
    function bringFocus(onReload: boolean): void {
      const focused = document.activeElement
      if (focused !== null && focused !== document.body) return
      const next = nextAction(dock.value, onReload)
      if (next) next.focus()
      else if (onReload && strip.offering.value) void nextTick(toOfferedReload)
      else focusScreenTitle()
    }
    // A screen typed in: the error that came offers «Обновить» under its words, and the row of the
    // version went for it — a focus that stood on the row's «Обновить» goes to that one, as it would to
    // the one in the strip (Ж of MOL-132; adversarial А7 of MOL-208). A tick later: the error's buttons
    // are drawn by a deferred `Teleport`, after the render that took the row away.
    function toOfferedReload(): void {
      const focused = document.activeElement
      if (focused !== null && focused !== document.body) return
      const reload = content.value?.querySelector<HTMLElement>('.reload')
      if (reload) reload.focus()
      else focusScreenTitle()
    }
    onUpdated(() => {
      if (focusInDock) bringFocus(focusOnReload)
      focusInDock = false
      focusOnReload = false
    })
    const tabbed = computed(() => Boolean(route.meta.tab))

    // Under a pinned row the title is gone once it passes the row's bottom edge; with the row
    // out of the page, once it passes the top of the window.
    const barHeight = useHeight(bar)
    const line = computed(() => (docked.value ? barHeight.value : 0))
    const collapsed = useCollapsed(sentinel, line)

    // Measured rather than guessed: the strip holds a total that grows a line when the queue is
    // not empty or the rate jumped, and the last row of a list must never end up under it.
    const dockHeight = useHeight(dock)
    // «Вернуть» stands over the end of the list for as long as a finger holds it: the list ends over
    // it too, as it did when «Покупки» had it in the strip (adversarial А1). Coming and going, it
    // moves only a list scrolled to its very end.
    const undo = ref<HTMLElement | null>(null)
    const undoHeight = useHeight(undo)
    const room = computed(() => {
      const over = undoHeight.value > 0 ? ` + ${String(undoHeight.value)}px + var(--space-2)` : ''
      return dockHeight.value > 0 || undoHeight.value > 0
        ? `calc(${String(dockHeight.value)}px${over} + var(--space-4))`
        : undefined
    })
    // What stands over the list — «Вернуть» — rises above the strip by its height.
    const dockStyle = computed(() =>
      dockHeight.value > 0 ? { '--dock-height': `${String(dockHeight.value)}px` } : undefined,
    )
    const update = usePwaUpdate()
    const updating = computed(() => update.phase.value !== 'none')
    // The row of the version swaps its button for words in its own render, which the frame does not
    // see: a version that did not take left the focus on the body (adversarial Д).
    watch(
      () => update.phase.value,
      () => {
        const focused = document.activeElement
        if (dock.value?.contains(focused))
          void nextTick(() => {
            bringFocus(isReload(focused))
          })
      },
      { flush: 'pre' },
    )
    const stateTarget = ref<HTMLElement | null>(null)
    const strip = provideStateStrip(stateTarget)
    // What the screen says of its answer silences the notice of an identity not checked (MOL-349).
    provideAnswerWord()
    // An error taking the strip unmounts the screen's own action, and a focus on it fell to the body
    // («Где вы?» closed over a failed «Что брать» hands it back to «Записать покупки», review №6): it
    // goes to the error's first button, where that action stood (`nextAction`). Where the action went
    // in the very render the error came in — «Что брать» draws its strip only for a newcomer — the
    // whole strip went before the error held it, and the focus with it (adversarial Г1): so whether
    // it stood in the strip is read before the frame patches, and a focus lost to the body is brought.
    watch(
      strip.held,
      (held) => {
        if (!held) return
        const focused = document.activeElement
        const lost = focused === null || focused === document.body
        if (!dock.value?.contains(focused) && !(focusInDock && lost)) return
        const onReload = lost ? focusOnReload : isReload(focused)
        void nextTick(() => nextAction(stateTarget.value, onReload)?.focus())
      },
      { flush: 'pre' },
    )
    // The error offers «Обновить» itself while a version waits or is being let in — from the strip
    // it holds, or under its own words on a screen typed in (`flow`, MOL-208).
    const offered = computed(
      () =>
        (strip.held.value || strip.offering.value) &&
        ['ready', 'applying'].includes(update.phase.value),
    )
    // The row of the version alone, stepped aside for the error's own «Обновить», leaves no strip.
    const banded = computed(() => updating.value && !offered.value)

    const { goBack } = useNavigation()

    return {
      t,
      bar,
      dock,
      content,
      undo,
      sentinel,
      collapsed,
      parentTitleKey,
      column,
      button,
      chevron,
      full,
      short,
      fit,
      parent,
      backWord,
      parentClass,
      docked,
      tabbed,
      room,
      dockStyle,
      banded,
      stateTarget,
      stateHeld: strip.held,
      offered,
      goBack,
    }
  },
})
</script>

<style scoped lang="scss">
/* A column as tall as the viewport, so that what fills the content — an empty or an offline
   state — can take the free height and put its action at the bottom, under the thumb. */
.screen {
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
}

.bar {
  @include pinned-bar;

  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 1;
  display: grid;

  /* At rest the small title is not shown, and its column is nothing: the back label has the row. */
  grid-template-columns: minmax(0, 1fr) 0 minmax(0, auto);
  align-items: center;
  gap: var(--space-2);
  height: calc(var(--bar-height) + var(--safe-top));
  padding: var(--safe-top) calc(var(--space-4) + var(--safe-right)) 0
    calc(var(--space-4) + var(--safe-left));
  border-bottom: var(--hairline) solid transparent;

  /* With nothing in it the row waits out of sight and takes no room. */
  opacity: 0;
  pointer-events: none;
  transition:
    opacity var(--dur) var(--ease),
    border-color var(--dur) var(--ease);
}

/* Something is in the row: it holds its place in the page and never moves. */
.docked .bar {
  position: sticky;
  opacity: 1;
  pointer-events: auto;
}

/* The title in the middle of the row, and the sides equal so that it is: never narrower than
   the chevron alone, whose button stands a --space-1 out of its column — 40 here is 44 to tap. */
.collapsed .bar {
  grid-template-columns:
    minmax(calc(var(--touch-target) - var(--space-1)), 1fr) minmax(0, auto)
    minmax(calc(var(--touch-target) - var(--space-1)), 1fr);
  opacity: 1;
  pointer-events: auto;
  border-bottom-color: var(--border);
}

.leading {
  position: relative;
  min-width: 0;
}

.trailing {
  display: flex;
  justify-content: flex-end;
  min-width: 0;
}

.meta {
  overflow: hidden;
  color: var(--text-muted);
  font-size: var(--text-footnote);
  font-weight: var(--weight-medium);
  white-space: nowrap;
  text-overflow: ellipsis;
  transition: opacity var(--dur) var(--ease);
}

.collapsed .meta {
  opacity: 0;
}

.small {
  margin: 0;
  overflow: hidden;
  font-size: var(--text-headline);
  font-weight: var(--weight-medium);
  white-space: nowrap;
  text-overflow: ellipsis;
  opacity: 0;
  transform: translateY(var(--space-2));
  transition:
    opacity var(--dur) var(--ease),
    transform var(--dur) var(--ease);
}

.collapsed .small {
  opacity: 1;
  transform: none;
}

.back {
  @include touch-target;

  justify-content: flex-start;
  gap: var(--space-1);
  max-width: 100%;
  margin-left: calc(var(--space-1) * -1);
  padding: 0;
  border: none;
  background: none;
  color: var(--accent-ink);
  font: inherit;
  font-weight: var(--weight-medium);
  white-space: nowrap;
  cursor: pointer;

  &:focus-visible {
    @include focus-ring;

    border-radius: var(--radius-sm);
  }
}

.chevron {
  @include icon;

  font-size: var(--icon-back);
}

/* The ladder keeps a label from being cut; this keeps one inside its column where nothing
   measures — a platform without ResizeObserver. */
.label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hidden {
  @include visually-hidden;
}

/* In the button's type, out of sight and out of the page's width. */
.samples {
  position: absolute;
  inset: 0;
  overflow: hidden;
  font-weight: var(--weight-medium);
  white-space: nowrap;
  visibility: hidden;
  pointer-events: none;

  > span {
    display: inline-block;
  }
}

.head {
  position: relative;
  padding: calc(var(--safe-top) + var(--space-4)) calc(var(--space-4) + var(--safe-right))
    var(--space-3) calc(var(--space-4) + var(--safe-left));
  border-bottom: var(--hairline) solid var(--border);
  background: var(--surface);
}

.docked .head {
  padding-top: 0;
}

.title {
  @include display-type;

  margin: 0;
  font-size: var(--text-display);
  line-height: var(--leading-tight);
  letter-spacing: -0.01em;

  /* An account's or a place's name may be one word wider than the phone, and the page went sideways
     (MOL-318). */
  overflow-wrap: anywhere;

  /* With large text a word wider than the line breaks where print breaks it, with a hyphen, from six letters — the
     newcomer's heading's rule (MOL-321, owner's В-2 «а»): at 150 % «Устройства» broke as «Устройст|ва», at 200 %
     «Катего|рии», «Бюдж|ет» (adversarial Л5). Never at 100 %, where no title breaks. Safari knows no limit, and WebKit
     hyphenates any word at a line's end — the price of the block button's hyphen, accepted there (self-review 13). */
  html[data-large-text] & {
    hyphens: auto;
    hyphenate-limit-chars: 6 3 3;
  }

  /* Focused by the router after a move, for screen readers; nothing for the eye to see. */
  &:focus {
    outline: none;
  }
}

.subtitle {
  @include appear(0);

  margin-top: var(--space-1);
  color: var(--text-muted);
  font-size: var(--text-footnote);
  overflow-wrap: anywhere;
}

/* Its bottom edge 24px below the top of the header: scrolled past it, the title counts as
   gone. Lifted by its own height because an observer still reports a box that only touches
   the line as intersecting — at the top edge the collapse would come a pixel late. */
.sentinel {
  position: absolute;
  top: var(--space-6);
  width: 1px;
  height: 1px;
  pointer-events: none;
  transform: translateY(-100%);
}

/* The home indicator is below every screen, not only under the tab bar: a nested screen has
   no bar to carry it, and its last row would sit under the indicator. */
.content {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: var(--space-4) calc(var(--space-4) + var(--safe-right))
    calc(var(--space-4) + var(--safe-bottom)) calc(var(--space-4) + var(--safe-left));

  /* What the screen puts in comes in — its answer in place of the skeleton above all (MOL-151);
     not on the screen's own arrival, which the view transition already plays. */
  > :slotted(*) {
    @include appear;
  }
}

.dock-room {
  flex: none;
}

/* Over the page, above the tab bar where there is one, and below the safe area where there is
   not: the same chrome as the pinned row at the top. Its margins and the column are its own, not
   each screen's (К-9): seven wrappers drew seven paddings, and «a main action, a ghost under it»
   came out 4 apart on one screen and 12 on the next. */
.dock {
  @include pinned-bar;
  @include appear(100%);

  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  display: grid;
  gap: var(--space-2);
  border-top: var(--hairline) solid var(--border);
  padding: var(--space-3) calc(var(--space-4) + var(--safe-right))
    calc(var(--space-3) + var(--safe-bottom)) calc(var(--space-4) + var(--safe-left));

  /* What the screen swaps in comes in, faded only: it stands under the thumb (MOL-151). Reached by
     `:deep`, not `:slotted`: a component of two roots — a button and its sheet, «Сфотографировать
     чек», «Записать вручную» — gets no slot attribute (adversarial А2). Not the strip's own top row,
     which comes in its own way, and not a sheet, which would replay it on every opening. */
  > :deep(:not(.update, dialog)) {
    @include appear(0);
  }
}

.tabbed .dock {
  bottom: calc(var(--tabbar-height) + var(--safe-bottom));
  padding-bottom: var(--space-3);
}

/* The strip's top row takes the strip's margins, and is parted from the screen's own action under
   it by 8 and a hairline (41 v2, 04). */
.dock > .update {
  padding: 0;
}

.dock > .over {
  padding-bottom: var(--space-2);
  border-bottom: var(--hairline) solid var(--border);
}

/* 8 over the strip, which on a nested screen already holds the home indicator; over the indicator
   where there is no strip, and over the tab bar on a section. Only what stands here takes taps. */
.undo-place {
  position: fixed;
  right: calc(var(--space-4) + var(--safe-right));
  bottom: calc(var(--safe-bottom) + var(--space-2));
  left: calc(var(--space-4) + var(--safe-left));
  z-index: 1;
  display: grid;
  gap: var(--space-2);
  pointer-events: none;

  /* `:deep` for the same reason as the strip's: a strip of two roots would be seen and not take a
     tap (adversarial А2′). */
  > :deep(*) {
    pointer-events: auto;
  }
}

.held .undo-place {
  bottom: calc(var(--dock-height) + var(--space-2));
}

.tabbed .undo-place {
  bottom: calc(var(--tabbar-height) + var(--safe-bottom) + var(--dock-height) + var(--space-2));
}

/* The notice keeps its own margins; this only keeps it out from under a notch held sideways. */
.notice-slot {
  padding: 0 var(--safe-right) 0 var(--safe-left);
}

.tabbed .content {
  padding-bottom: calc(var(--tabbar-height) + var(--safe-bottom) + var(--space-8));
}

@media (prefers-reduced-motion: reduce) {
  .bar,
  .meta,
  .small {
    transition: none;
  }
}
</style>
